import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Sun, Moon, ShieldCheck, PiggyBank } from 'lucide-react';
import { IMG } from '../constants/site';
import { useLanguage } from '../i18n/LanguageContext';
import { useSiteImage } from '../components/SiteImages';
import { useSEO } from '../components/SEO';
import { Reveal, MaskedTitle, Overline, EASE } from '../components/motion';
import { LeadForm } from '../components/LeadForm';

const BENEFIT_ICONS = [Sun, Moon, ShieldCheck, PiggyBank];

// Lamella graphics cut 1:1 from the supplier colour chart — Standard & Extra Standard ranges
const RULOURI_COLOR_GROUPS = [
  {
    key: 'standard',
    colors: [
      { img: '/rulouri-color-alb.webp', nameIdx: 0, code: 'RAL 9016' },
      { img: '/rulouri-color-maro.webp', nameIdx: 4, code: 'RAL 8019' },
      { img: '/rulouri-color-antracit.webp', nameIdx: 3, code: 'RAL 7016' },
      { img: '/rulouri-color-nuc.webp', nameIdx: 7, code: '' },
    ],
  },
  {
    key: 'extra',
    colors: [
      { img: '/rulouri-color-crem.webp', nameIdx: 1, code: 'RAL 9001' },
      { img: '/rulouri-color-silver.webp', nameIdx: 2, code: 'RAL 9006' },
      { img: '/rulouri-color-golden-oak.webp', nameIdx: 5, code: 'Golden Oak' },
      { img: '/rulouri-color-nuc-extra.webp', nameIdx: 8, code: '' },
    ],
  },
];

export default function Rulouri() {
  const { t } = useLanguage();
  const r = t('rulouri');
  useSEO('rulouri');
  const { scrollY } = useScroll();
  const imgY = useTransform(scrollY, [0, 500], [0, 80]);
  const heroImg = useSiteImage('hero.rulouri', IMG.house2);

  return (
    <main data-testid="rulouri-page">
      <section className="relative min-h-[85svh] flex items-center overflow-hidden pt-20">
        <motion.div style={{ y: imgY }} className="absolute inset-0 -bottom-24">
          <motion.img
            src={heroImg}
            alt={r.title}
            className="w-full h-full object-cover"
            initial={{ scale: 1.1 }}
            animate={{ scale: 1 }}
            transition={{ duration: 2, ease: 'easeOut' }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/25" />
        </motion.div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12 w-full py-24">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1, ease: EASE }}>
            <Overline className="mb-8">{r.overline}</Overline>
          </motion.div>
          <MaskedTitle
            lines={[r.title]}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.02] mb-8 max-w-3xl text-white"
          />
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.5, ease: EASE }}
            className="text-white/75 text-base md:text-lg max-w-2xl leading-relaxed mb-10"
          >
            {r.desc}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.65, ease: EASE }}>
            <a
              href="#rulouri-form"
              data-testid="rulouri-hero-cta"
              className="inline-flex items-center gap-3 bg-accent text-white rounded-full px-8 py-4 text-[11px] font-bold uppercase tracking-[0.22em] hover:gap-5 transition-[gap] duration-300"
            >
              {t('nav.cta')} <ArrowRight size={15} />
            </a>
          </motion.div>
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-ink/10 border border-line/10">
          {r.benefits.map((b, i) => {
            const Icon = BENEFIT_ICONS[i] || Sun;
            return (
              <Reveal key={i} delay={i * 0.08}>
                <div data-testid={`rulouri-benefit-${i}`} className="bg-paper p-5 md:p-8 h-full hover:bg-surface transition-colors duration-500">
                  <Icon size={20} className="text-accent" strokeWidth={1.6} />
                  <h3 className="font-display text-base md:text-lg font-medium mt-4 md:mt-6 mb-2 md:mb-3">{b.t}</h3>
                  <p className="text-smoke text-xs md:text-sm leading-relaxed">{b.d}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-6 md:px-12 pb-20 md:pb-28">
        <Reveal className="mb-12 md:mb-16">
          <Overline className="mb-4">{r.systemsOverline}</Overline>
          <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mb-4">{r.systemsTitle}</h2>
          <p className="text-smoke max-w-2xl leading-relaxed">{r.systemsDesc}</p>
        </Reveal>

        {/* Vertical editorial rows with LARGE graphics (stacked on mobile) */}
        <div className="flex flex-col gap-14 md:gap-20 lg:gap-28">
          {r.systems.map((sys, i) => (
            <Reveal key={i} delay={0.05}>
              <div
                data-testid={`rulouri-system-${i}`}
                className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 lg:gap-16 items-center ${i % 2 ? 'md:[&>*:first-child]:order-2' : ''}`}
              >
                <div
                  data-testid={`rulouri-system-graphic-${i}`}
                  className="group/img col-span-1 md:col-span-5 aspect-square bg-white rounded-[2rem] border border-line/10 shadow-[0_20px_60px_rgba(24,26,32,0.08)] overflow-hidden p-6 lg:p-8"
                >
                  <img
                    src={`/rulouri-system-${i}.webp`}
                    alt={sys.name}
                    loading="lazy"
                    className="w-full h-full object-contain transition-transform duration-700 group-hover/img:scale-[1.04]"
                  />
                </div>
                <div className="col-span-1 md:col-span-7 relative">
                  <span aria-hidden="true" className="pointer-events-none select-none absolute -top-12 lg:-top-16 -left-2 font-display text-8xl lg:text-9xl font-semibold text-ink/5 leading-none">
                    {(i + 1).toString().padStart(2, '0')}
                  </span>
                  <div className="relative">
                    <h3 className="font-display text-2xl md:text-3xl lg:text-4xl font-medium tracking-tight leading-snug mb-4 md:mb-5">{sys.name}</h3>
                    <p className="text-smoke text-base md:text-lg leading-relaxed max-w-xl">{sys.d}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-surface2 border-y border-line/10">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 md:py-28">
          <Reveal className="mb-12 md:mb-16">
            <Overline className="mb-4">{r.colorsOverline}</Overline>
            <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mb-4">{r.colorsTitle}</h2>
            <p className="text-smoke max-w-2xl leading-relaxed">{r.colorsDesc}</p>
          </Reveal>
          <div className="flex flex-col gap-14 md:gap-20">
            {RULOURI_COLOR_GROUPS.map((group) => (
              <div key={group.key}>
                <Reveal className="mb-8 flex items-center gap-4">
                  <h3 data-testid={`rulouri-colors-${group.key}-title`} className="font-display text-xl md:text-2xl font-medium tracking-tight">
                    {group.key === 'standard' ? r.standardTitle : r.extraTitle}
                  </h3>
                  <span className="flex-1 h-px bg-line/15" />
                </Reveal>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                  {group.colors.map((c, i) => (
                    <Reveal key={c.img} delay={i * 0.05}>
                      <div
                        data-testid={`rulouri-color-${group.key}-${i}`}
                        className="group bg-white border border-line/10 rounded-[2rem] overflow-hidden hover:shadow-[0_24px_70px_rgba(24,26,32,0.10)] hover:-translate-y-1 transition-[box-shadow,transform] duration-500"
                      >
                        <div className="h-36 md:h-44 grid place-items-center p-4">
                          <img
                            src={c.img}
                            alt={`${r.colorNames[c.nameIdx]} ${c.code}`}
                            loading="lazy"
                            className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-[1.05]"
                          />
                        </div>
                        <div className="px-5 pb-5 text-right">
                          <p className="font-display font-medium text-sm">{r.colorNames[c.nameIdx]}</p>
                          <p className="text-smoke text-xs mt-0.5">{c.code || ' '}</p>
                        </div>
                      </div>
                    </Reveal>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <Reveal className="mt-8">
            <p data-testid="rulouri-colors-note" className="text-smoke text-sm border-l-2 border-accent pl-4">{r.colorsNote}</p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 md:py-28">
        <Reveal className="mb-12 md:mb-16">
          <Overline className="mb-4">{r.motorsOverline}</Overline>
          <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mb-4">{r.motorsTitle}</h2>
          <p className="text-smoke max-w-2xl leading-relaxed">{r.motorsDesc}</p>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {r.motors.map((m, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <div
                data-testid={`rulouri-motor-${i}`}
                className="group bg-surface border border-line/10 rounded-[2rem] p-6 md:p-7 h-full hover:shadow-[0_24px_70px_rgba(24,26,32,0.10)] hover:-translate-y-1 transition-[box-shadow,transform] duration-500"
              >
                <span className="font-display text-sm text-accent tracking-[0.2em]">0{i + 1}</span>
                <h3 className="font-display text-lg font-medium tracking-tight mt-3 mb-4 leading-snug">{m.name}</h3>
                <ul className="space-y-2">
                  {m.f.map((feat, j) => (
                    <li key={j} className="text-smoke text-sm leading-relaxed flex gap-2.5">
                      <span className="mt-[9px] w-3 h-px bg-accent shrink-0" />
                      {feat}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section id="rulouri-form" className="max-w-[1440px] mx-auto px-6 md:px-12 pb-20 md:pb-28 scroll-mt-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <Reveal>
              <Overline className="mb-4">{t('nav.cta')}</Overline>
              <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight leading-tight">{r.ctaTitle}</h2>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="lg:col-span-7">
            <div className="bg-paper border border-line/10 rounded-[2rem] p-6 sm:p-8 md:p-10 shadow-[0_30px_90px_rgba(24,26,32,0.10)]">
              <LeadForm testIdPrefix="rulouri-lead-form" defaultServices={['rulouri']} />
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
